import { useMutationCheckEligibilityRuleQuery } from '@ben/columbia/api';
import RiDownload2Line from '@ben/icons/download-2-line';
import Button from '@ben/orion/button';
import Input, { InputVariant } from '@ben/orion/input';
import Tooltip from '@ben/orion/tooltip';
import type { IModularBenefitDetail } from '@ben/shared/types/columbia';
import { exportCsvFile } from '@ben/shared/utils/export-csv-file';
import { isNumber } from 'lodash';
import { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';

const AUTO_ENROLMENT_ELIGIBILITY_QUERY_HINT =
  'Optional. When set, only employees matching this formula are auto-enrolled. Benefit eligibility rules still control who can see and opt into the benefit. Example for UK pensions: AGE_GTE(22) and SALARY_GTE(10000)';

export const AutoEnrolmentEligibilityQuery = () => {
  const { control, getValues, setError, clearErrors } =
    useFormContext<IModularBenefitDetail>();
  const { mutate: checkEligibilityRule, isLoading: isTestingQuery } =
    useMutationCheckEligibilityRuleQuery();
  const [queryValidated, setQueryValidated] = useState(false);

  const employerId = useWatch({
    control,
    name: 'employer',
    exact: true,
  });
  const hasEmployer = isNumber(employerId);

  const handleTestQuery = () => {
    const query = getValues('enrollmentConfig.eligibilityQuery')?.trim() ?? '';

    if (!query) {
      clearErrors('enrollmentConfig.eligibilityQuery');
      setQueryValidated(false);
      return;
    }

    if (!hasEmployer) {
      setError('enrollmentConfig.eligibilityQuery', {
        type: 'manual',
        message: 'Select an employer before testing the query.',
      });
      return;
    }

    checkEligibilityRule(
      { employerId: Number(employerId), query },
      {
        onSuccess: (response: string) => {
          clearErrors('enrollmentConfig.eligibilityQuery');
          setQueryValidated(true);
          exportCsvFile({
            data: response,
            fileName: 'auto-enrolment-query-result',
          });
        },
        onError: (error) => {
          setQueryValidated(false);
          setError('enrollmentConfig.eligibilityQuery', {
            type: 'manual',
            message: error?.response?.data?.detail,
          });
        },
      }
    );
  };

  return (
    <Container>
      <Controller
        name="enrollmentConfig.eligibilityQuery"
        control={control}
        render={({ field, fieldState }) => {
          const { value, onChange, ...rest } = field;

          return (
            <Input
              {...rest}
              name="Auto-enrolment eligibility query"
              hintText={AUTO_ENROLMENT_ELIGIBILITY_QUERY_HINT}
              value={value ?? ''}
              width="100%"
              variant={InputVariant.TextArea}
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              data-testid="auto-enrolment-eligibility-query"
              onChange={(event) => {
                onChange(event);
                setQueryValidated(false);
                clearErrors('enrollmentConfig.eligibilityQuery');
              }}
            />
          );
        }}
      />
      <Tooltip
        tooltipText="Assign an employer to this benefit before testing the query"
        noAnchorRole
        hideTooltip={hasEmployer}
      >
        <Button
          leftIcon={RiDownload2Line}
          loading={isTestingQuery}
          onClick={handleTestQuery}
          disabled={!hasEmployer}
          variant="outline"
          w={164}
        >
          {queryValidated ? 'Re-test query' : 'Test query'}
        </Button>
      </Tooltip>
    </Container>
  );
};

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `};
`;
